import type { CheckoutParticipantAgeCategory, CheckoutServiceOption, CheckoutServiceParticipant } from '@/scopes/checkout/domain/types'

export interface PerBookingPriceRow {
  ageCategory: CheckoutParticipantAgeCategory
  prices: number[]
}

// servicePartcipantsMapper maps age category doing a lower case. This is a workaround for now.
export const AGE_CATEGORY_TRANSLATION_KEY: Record<CheckoutParticipantAgeCategory | 'adult' | 'child' | 'baby', string> = {
  Adult: 'checkout_steps_ancillaries_generic_service_age_category_adult',
  Child: 'checkout_steps_ancillaries_generic_service_age_category_child',
  Baby: 'checkout_steps_ancillaries_generic_service_age_category_baby',
  adult: 'checkout_steps_ancillaries_generic_service_age_category_adult',
  child: 'checkout_steps_ancillaries_generic_service_age_category_child',
  baby: 'checkout_steps_ancillaries_generic_service_age_category_baby',
}

// One row per FE age category with distinct prices ascending; empty when no assignment resolves to a known participant.
export function getPricesPerAgeCategory(
  option: Pick<CheckoutServiceOption, 'priceAssignments'>,
  participants: Record<string, Pick<CheckoutServiceParticipant, 'ageCategory'>> = {},
): PerBookingPriceRow[] {
  const pricesByCategory = new Map<CheckoutParticipantAgeCategory, Set<number>>()

  ;(option.priceAssignments ?? []).forEach(({ participantIds, price }) => {
    participantIds.forEach((id) => {
      const category = participants[id]?.ageCategory
      if (!category)
        return
      const existing = pricesByCategory.get(category) ?? new Set<number>()
      existing.add(price)
      pricesByCategory.set(category, existing)
    })
  })

  return Array.from(pricesByCategory.entries()).map(([category, prices]) => ({
    ageCategory: category,
    prices: Array.from(prices).sort((a, b) => a - b),
  }))
}

// prices must already be sorted ascending; collapses to a min-max range when more than one distinct value exists.
export function formatPriceRowText(
  prices: number[],
  formatPrice: (price: number) => string,
  formatPriceRange: (minimumPrice: number, maximumPrice: number) => string,
): string {
  if (prices.length > 1)
    return formatPriceRange(prices[0], prices[prices.length - 1])

  return formatPrice(prices[0])
}

// Falls back to the option's flat price when there's no participantId, no assignments, or no match for the participant.
export function getPriceForParticipant(
  option: Partial<Pick<CheckoutServiceOption, 'price' | 'priceAssignments'>> | undefined,
  participantId?: string,
): number {
  const { price = 0, priceAssignments = [] } = option ?? {}

  if (!participantId || priceAssignments.length === 0)
    return price

  return priceAssignments.find(({ participantIds }) => participantIds.includes(participantId))?.price ?? price
}
